import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const MX_GRADIENT = { background: "linear-gradient(135deg, #6C47FF 0%, #E91E8C 100%)" };

export default function ForceChangePassword() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Password updated — welcome!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — Form */}
      <div className="flex-1 bg-[#F0F1F7] flex flex-col justify-center px-10 py-12 lg:px-16 max-w-[560px]">
        <div className="mb-10">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-[10px] flex items-center justify-center text-white font-[900] text-lg flex-shrink-0"
              style={MX_GRADIENT}
            >
              M
            </div>
            <div>
              <div className="text-[15px] font-[800] tracking-tight text-[#1A1A2E] uppercase leading-none">
                MX Academy
              </div>
              <div className="text-[9px] font-[600] text-[#9CA3AF] tracking-[0.18em] uppercase mt-1">
                Learning Hub
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6 p-4 rounded-[12px] bg-amber-50 border border-amber-200">
          <p className="text-sm font-[700] text-amber-800">Action required</p>
          <p className="text-sm text-amber-700 mt-1">
            Your account is using a default password. Please set a new password before continuing.
          </p>
        </div>

        <h1 className="text-[32px] font-[900] text-[#1A1A2E] leading-tight mb-2">Set a new password.</h1>
        <p className="text-sm text-[#6B7280] mb-8">Choose a strong password you haven't used before.</p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-[10px] font-[700] text-[#6B7280] mb-2 uppercase tracking-[0.12em]">
              Current password
            </label>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              placeholder="Your current password"
              className="w-full rounded-[10px] border border-[#E8EAEF] px-4 py-3 min-h-[48px] bg-white focus:outline-none focus:border-[#6C47FF] text-sm text-[#1A1A2E] placeholder-[#C4C9DA]"
            />
          </div>

          <div>
            <label className="block text-[10px] font-[700] text-[#6B7280] mb-2 uppercase tracking-[0.12em]">
              New password
            </label>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={8}
              placeholder="At least 8 characters"
              className="w-full rounded-[10px] border border-[#E8EAEF] px-4 py-3 min-h-[48px] bg-white focus:outline-none focus:border-[#6C47FF] text-sm text-[#1A1A2E] placeholder-[#C4C9DA]"
            />
          </div>

          <div>
            <label className="block text-[10px] font-[700] text-[#6B7280] mb-2 uppercase tracking-[0.12em]">
              Confirm new password
            </label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              placeholder="Repeat new password"
              className="w-full rounded-[10px] border border-[#E8EAEF] px-4 py-3 min-h-[48px] bg-white focus:outline-none focus:border-[#6C47FF] text-sm text-[#1A1A2E] placeholder-[#C4C9DA]"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full text-white rounded-[12px] min-h-[52px] py-3 font-[700] text-sm transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
            style={MX_GRADIENT}
          >
            {loading ? "Updating…" : "Set new password →"}
          </button>
        </form>
      </div>

      {/* Right Panel */}
      <div
        className="hidden lg:flex flex-1 flex-col justify-center px-16 py-12 text-white relative overflow-hidden"
        style={MX_GRADIENT}
      >
        <div className="absolute top-[-80px] right-[-80px] w-[320px] h-[320px] rounded-full bg-white/10 pointer-events-none" />
        <div className="absolute bottom-[-60px] left-[-60px] w-[240px] h-[240px] rounded-full bg-white/10 pointer-events-none" />
        <div className="relative z-10">
          <h2 className="text-[48px] font-[900] leading-[1.05] mb-6">Keep your<br />account safe.</h2>
          <p className="text-white/80 text-base leading-relaxed max-w-sm">
            A strong, unique password is the first line of defence for your leads data.
          </p>
        </div>
      </div>
    </div>
  );
}
